import { useState, useEffect, useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import smmApi from '../../../services/smmApi';

// Define theme colors for platforms
const platformColors: Record<string, { text: string; bg: string; border: string; icon: string }> = {
  LinkedIn: {
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: 'ri-linkedin-box-fill text-[#0a66c2]'
  },
  Instagram: {
    text: 'text-pink-700',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    icon: 'ri-instagram-line text-[#e1306c]'
  },
  YouTube: {
    text: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'ri-youtube-fill text-[#ff0000]'
  },
  Facebook: {
    text: 'text-blue-800',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'ri-facebook-box-fill text-[#1877f2]'
  },
  Twitter: {
    text: 'text-gray-800',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    icon: 'ri-twitter-x-fill text-gray-900'
  }
};

const alertMeta: Record<string, { bg: string; border: string; badgeBg: string; badgeText: string }> = {
  critical: { bg: 'bg-red-50/70', border: 'border-red-200/80', badgeBg: 'bg-red-100', badgeText: 'text-red-700' },
  warning: { bg: 'bg-amber-50/70', border: 'border-amber-200/80', badgeBg: 'bg-amber-100', badgeText: 'text-amber-700' },
  info: { bg: 'bg-sky-50/70', border: 'border-sky-200/80', badgeBg: 'bg-sky-100', badgeText: 'text-sky-700' },
  deviation: { bg: 'bg-violet-50/70', border: 'border-violet-200/80', badgeBg: 'bg-violet-100', badgeText: 'text-violet-700' },
};

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

// "2026-10" → "October 2026" (built from parts: "2026-10-01" parses as UTC and can land in September).
function monthLabel(yyyymm: string) {
  const [y, m] = yyyymm.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// The Social Presence endpoint answers in camelCase (healthScore,
// engagementHistory); this page reads snake_case — without this every
// platform showed score 0, "LIVE — Unknown" growth and a flat trend.
function snakeKeys(value: any): any {
  if (Array.isArray(value)) return value.map(snakeKeys);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`), snakeKeys(v)]),
    );
  }
  return value;
}

function normalizePlatformName(name: string): string {
  const map: Record<string, string> = {
    linkedin: 'LinkedIn',
    instagram: 'Instagram',
    facebook: 'Facebook',
    youtube: 'YouTube',
    twitter: 'Twitter',
  };
  return map[name.toLowerCase()] || (name.charAt(0).toUpperCase() + name.slice(1));
}

// Circular progress for campaign rate
function CircularProgress({ value, size = 52 }: { value: number; size?: number }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const dash = (value / 100) * c;
  const cx = size / 2;
  return (
    <svg width={size} height={size} className="block">
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="#f3f4f6" strokeWidth="3" />
      <circle
        cx={cx} cy={cx} r={r} fill="none" stroke="#5C4FCF" strokeWidth="3.5"
        strokeDasharray={`${dash} ${c - dash}`} strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cx})`}
      />
    </svg>
  );
}

// Sparkline for platform matrices — "No history yet" under two points, not a flat line at 0
function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) {
    return <p className="text-[10px] font-semibold text-gray-400 h-6 flex items-center">No history yet</p>;
  }
  const mx = Math.max(...data);
  const mn = Math.min(...data);
  const range = mx - mn || 1;
  const w = 110;
  const h = 24;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - mn) / range) * h}`).join(' ');
  return (
    <svg width={w} height={h} className="block overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ThemeBar for Theme Intelligence (Double horizontal back-to-back progress bar)
function ThemeBar({ best, low }: { best: number; low: number }) {
  return (
    <div className="flex items-center gap-1.5 h-2.5 w-full">
      {/* Best (Indigo/Purple) - grows center to left */}
      <div className="h-full rounded-l-full bg-gray-100 flex-1 relative overflow-hidden flex justify-end">
        <div className="h-full bg-indigo-500 rounded-l-full" style={{ width: `${best}%` }} />
      </div>
      {/* Low (Pink/Red) - grows center to right */}
      <div className="h-full rounded-r-full bg-gray-100 flex-1 relative overflow-hidden">
        <div className="h-full bg-red-400 rounded-r-full" style={{ width: `${low}%` }} />
      </div>
    </div>
  );
}

// Banner for "no plan yet" (info) or a failed load (error, with Retry).
function StatusBanner({ tone, title, message, onRetry }: { tone: 'info' | 'error'; title: string; message?: string; onRetry?: () => void }) {
  const isError = tone === 'error';
  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border ${isError ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
      <i className={`${isError ? 'ri-error-warning-line text-red-500' : 'ri-information-line text-amber-600'} text-lg leading-none mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${isError ? 'text-red-800' : 'text-amber-800'}`}>{title}</p>
        {message && <p className={`text-xs mt-0.5 ${isError ? 'text-red-700' : 'text-amber-700'}`}>{message}</p>}
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-red-200 text-red-700 hover:bg-red-100 transition-colors whitespace-nowrap"
        >
          Retry
        </button>
      )}
    </div>
  );
}

const isPostCompleted = (p: any) => {
  const gov = String(p?.governance_status || '').toUpperCase();
  const st = String(p?.status || '').toUpperCase();
  if (gov === 'NOT_CREATED') return false;
  return gov === 'DONE' || gov === 'POSTED' || gov === 'SCHEDULED_MANUAL' || st === 'DONE' || st === 'POSTED';
};

export default function GovernanceDashboard() {
  const [selectedPlatform, setSelectedPlatform] = useState<string>('Overall');
  const [dbSummary, setDbSummary] = useState<any>(null);
  const [dbPosts, setDbPosts] = useState<any[]>([]);
  const [presenceData, setPresenceData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // 'no-plan' = no calendar for this month (404); 'error' = the load failed.
  const [govStatus, setGovStatus] = useState<'ok' | 'no-plan' | 'error'>('ok');
  const [govError, setGovError] = useState('');
  const [presenceError, setPresenceError] = useState('');
  // The summary (alerts) failed while the posts loaded: the post-based numbers still show.
  const [summaryError, setSummaryError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const userId = localStorage.getItem('user_id') || '';
  const month = currentMonth();

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.allSettled([
      smmApi.getSmmGovernanceSummary(userId, month),
      smmApi.getSmmGovernancePosts(userId, month),
      smmApi.getSocialPresence(userId),
    ]).then(([summaryRes, postsRes, presenceRes]) => {
      setDbSummary(summaryRes.status === 'fulfilled' ? summaryRes.value : null);
      setDbPosts(postsRes.status === 'fulfilled' ? postsRes.value || [] : []);
      setPresenceData(presenceRes.status === 'fulfilled' ? snakeKeys(presenceRes.value) || null : null);

      // The numbers come from the posts (the summary only adds alerts), so a
      // failed summary must not hide posts that loaded.
      const isNotFound = (r: PromiseSettledResult<any>) =>
        r.status === 'rejected' && r.reason?.response?.status === 404;
      if (postsRes.status === 'rejected' && !isNotFound(postsRes)) {
        setGovStatus('error');
        setGovError(postsRes.reason?.message || "Couldn't load this month's posts.");
      } else {
        setGovStatus(isNotFound(postsRes) ? 'no-plan' : 'ok');
        setGovError('');
      }
      setSummaryError(
        summaryRes.status === 'rejected' && !isNotFound(summaryRes) && postsRes.status === 'fulfilled'
          ? summaryRes.reason?.message || "Couldn't load this month's alerts."
          : ''
      );
      setPresenceError(
        presenceRes.status === 'rejected'
          ? presenceRes.reason?.message || "Couldn't load platform data."
          : ''
      );
    }).finally(() => {
      setLoading(false);
    });
  }, [userId, month, reloadKey]);

  const govOk = govStatus === 'ok';
  // Shown in place of "nothing here" texts when governance data is missing.
  const govEmptyText =
    govStatus === 'no-plan' ? 'No content plan for this month yet.' : "Couldn't load this month's posts.";
  const loadErrors = Array.from(new Set([govStatus === 'error' ? govError : '', summaryError, presenceError].filter(Boolean)));
  // The backend falls back to the latest calendar when this month has none; it
  // reports that calendar's real month so we can say which month is shown.
  const calendarMonth: string = dbPosts[0]?.calendar_month || dbSummary?.month || month;
  const isOtherMonth = govOk && calendarMonth !== month;

  // Compute execution overview metrics dynamically based on selected platform
  const execStats = useMemo(() => {
    if (dbPosts && dbPosts.length > 0) {
      const filtered = dbPosts.filter((p: any) =>
        selectedPlatform === 'Overall' ||
        p.platform?.toLowerCase() === selectedPlatform.toLowerCase()
      );

      const totalPlanned = filtered.length;
      const completed = filtered.filter(isPostCompleted).length;
      const pending = filtered.filter((p: any) => !isPostCompleted(p)).length;
      const overdue = filtered.filter((p: any) => p.is_overdue && !isPostCompleted(p)).length;

      const campaignPosts = filtered.filter((p: any) => p.is_campaign_post);
      const totalCampaign = campaignPosts.length;
      const completedCampaign = campaignPosts.filter(isPostCompleted).length;
      const campaignRate = totalCampaign > 0 ? Math.round((completedCampaign / totalCampaign) * 100) : null;

      return {
        total_planned: totalPlanned,
        completed,
        pending,
        overdue,
        campaign: totalCampaign > 0 ? {
          total: totalCampaign,
          completed: completedCampaign,
          campaign_rate: campaignRate
        } : null
      };
    }

    if (dbSummary && selectedPlatform === 'Overall') {
      return dbSummary;
    }

    return { total_planned: 0, completed: 0, pending: 0, overdue: 0, campaign: null };
  }, [dbPosts, dbSummary, selectedPlatform]);

  const completionRate = execStats.total_planned > 0
    ? Math.round((execStats.completed / execStats.total_planned) * 100)
    : 0;

  // Dynamic Platform Intelligence Matrix — built from presence API platforms
  const platformMatrices = useMemo(() => {
    if (!presenceData?.platforms?.length) return [];
    return presenceData.platforms.map((plat: any) => {
      const normalizedName = normalizePlatformName(plat.name);
      const history: any[] = presenceData.engagement_history || [];
      const trend = history
        .map((h: any) => h[plat.name] as number | null)
        .filter((v): v is number => v != null);
      const growthSign = plat.trend === 'down' ? '-' : '+';
      const growthVal =
        plat.trend_value != null && plat.trend_value > 0
          ? `${growthSign}${plat.trend_value}%`
          : plat.trend === 'stable'
            ? 'Stable'
            : '—';
      return {
        name: normalizedName,
        // null (shown "—") when the platform has no health score yet, not 0/100
        score: typeof plat.health_score === 'number' ? plat.health_score : null,
        trend,
        growth: growthVal,
        color: plat.color || '#71717a',
        followers: plat.followers,
      };
    });
  }, [presenceData]);

  // Dynamic chart data — built from presence API engagement_history.
  // No history means no points: the chart says "Not enough history yet" rather than inventing a ramp,
  // and a month with no value is a gap (null), not a 0.
  const chartData = useMemo(() => {
    const history: any[] = presenceData?.engagement_history || [];
    const platKeys = ['instagram', 'facebook', 'linkedin', 'youtube'];

    if (!history.length) return [];

    if (selectedPlatform === 'Overall') {
      return history.map((h: any) => {
        const values = platKeys.map((k) => h[k]).filter((v: any): v is number => v != null);
        const avgEngagement =
          values.length > 0
            ? Math.round((values.reduce((a: number, b: number) => a + b, 0) / values.length) * 10) / 10
            : null;
        return { month: h.month, engagement: avgEngagement };
      });
    }

    const platKey = selectedPlatform.toLowerCase();
    return history.map((h: any) => ({
      month: h.month,
      engagement: h[platKey] ?? null,
    }));
  }, [presenceData, selectedPlatform]);
  const chartPointCount = chartData.filter((d: any) => d.engagement != null).length;

  // Dynamic sentiment — built from presence API platform sentiment scores. Only
  // platforms that have sentiment count (a platform without any isn't 0 % positive);
  // null when none has, shown as "—".
  const sentiment = useMemo((): { positive: number; neutral: number; negative: number } | null => {
    const platforms: any[] = presenceData?.platforms || [];

    if (selectedPlatform === 'Overall') {
      const withData = platforms.filter((p: any) => p.sentiment);
      if (!withData.length) return null;
      const totals = withData.reduce(
        (acc: any, p: any) => {
          acc.positive += p.sentiment.positive || 0;
          acc.neutral += p.sentiment.neutral || 0;
          acc.negative += p.sentiment.negative || 0;
          return acc;
        },
        { positive: 0, neutral: 0, negative: 0 }
      );
      const n = withData.length;
      return {
        positive: Math.round(totals.positive / n),
        neutral: Math.round(totals.neutral / n),
        negative: Math.round(totals.negative / n),
      };
    }

    const found = platforms.find(
      (p: any) => p.name.toLowerCase() === selectedPlatform.toLowerCase()
    );
    if (!found?.sentiment) return null;
    return {
      positive: found.sentiment.positive || 0,
      neutral: found.sentiment.neutral || 0,
      negative: found.sentiment.negative || 0,
    };
  }, [presenceData, selectedPlatform]);

  // "—" when the presence data didn't load or there's no sentiment yet — never 0%.
  const sentimentPct = (key: 'positive' | 'neutral' | 'negative') =>
    presenceError || !sentiment ? '—' : `${sentiment[key]}%`;

  const conicGradientStr = sentiment
    ? `conic-gradient(#5C4FCF 0% ${sentiment.positive}%, #c084fc ${sentiment.positive}% ${sentiment.positive + sentiment.neutral}%, #fca5a5 ${sentiment.positive + sentiment.neutral}% 100%)`
    : '#f3f4f6';

  // Recently completed posts — completed posts from governance API, newest scheduled date first.
  // (No engagement is fetched for these, so they're not ranked or labelled as "top performing".)
  const filteredPerformancePosts = useMemo(() => {
    const done = dbPosts
      .filter(isPostCompleted)
      .filter((p: any) =>
        selectedPlatform === 'Overall' ||
        p.platform?.toLowerCase() === selectedPlatform.toLowerCase()
      );

    const sorted = [...done].sort((a: any, b: any) =>
      (b.scheduled_date || '').localeCompare(a.scheduled_date || '')
    );

    return sorted.slice(0, 4).map((p: any) => ({
      id: p.post_id,
      title: p.weekly_theme_label || p.theme_type || 'Post',
      platform: normalizePlatformName(p.platform || ''),
      scheduledDate: p.scheduled_date || null,
    }));
  }, [dbPosts, selectedPlatform]);

  // Dynamic theme intelligence — calculated from completed posts grouped by theme_type
  const themeAnalysis = useMemo(() => {
    const filtered = dbPosts.filter((p: any) =>
      selectedPlatform === 'Overall' ||
      p.platform?.toLowerCase() === selectedPlatform.toLowerCase()
    );
    const completed = filtered.filter(isPostCompleted);
    const incomplete = filtered.filter((p: any) => !isPostCompleted(p));

    if (completed.length < 5) {
      return { insufficientData: true, themes: [] };
    }

    const themeCompletedMap: Record<string, number> = {};
    const themeIncompleteMap: Record<string, number> = {};

    for (const p of completed) {
      const t: string = p.theme_type || 'Other';
      themeCompletedMap[t] = (themeCompletedMap[t] || 0) + 1;
    }
    for (const p of incomplete) {
      const t: string = p.theme_type || 'Other';
      themeIncompleteMap[t] = (themeIncompleteMap[t] || 0) + 1;
    }

    const allThemes = new Set([
      ...Object.keys(themeCompletedMap),
      ...Object.keys(themeIncompleteMap),
    ]);
    const totalCompleted = completed.length;
    const totalIncomplete = incomplete.length;

    const themes = Array.from(allThemes).map((theme) => ({
      name: theme,
      // best = % of completed posts that are this theme (completion composition)
      best:
        totalCompleted > 0
          ? Math.round(((themeCompletedMap[theme] || 0) / totalCompleted) * 100)
          : 0,
      // low = % of incomplete posts that are this theme (backlog composition)
      low:
        totalIncomplete > 0
          ? Math.round(((themeIncompleteMap[theme] || 0) / totalIncomplete) * 100)
          : 0,
    }));

    return { insufficientData: false, themes };
  }, [dbPosts, selectedPlatform]);

  // Dynamic operational alerts — from backend summary payload
  const filteredAlerts = useMemo(() => {
    const alerts: any[] = dbSummary?.operational_alerts || [];
    return alerts.filter((alert: any) =>
      selectedPlatform === 'Overall' ||
      alert.platform?.toLowerCase() === selectedPlatform.toLowerCase()
    );
  }, [dbSummary, selectedPlatform]);

  // Leading platform by health score for aggregate health blurb (only platforms that have a score)
  const leadingPlatform = useMemo(() => {
    const platforms: any[] = (presenceData?.platforms || []).filter((p: any) => typeof p.health_score === 'number');
    if (!platforms.length) return null;
    const top = [...platforms].sort((a: any, b: any) => b.health_score - a.health_score)[0];
    return top ? normalizePlatformName(top.name) : null;
  }, [presenceData]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Filter bar skeleton */}
        <div className="flex bg-white/80 p-1.5 rounded-xl border border-gray-100 shadow-sm max-w-max gap-1">
          {[...Array(5)].map((_, i) => (
            <div key={i} className={`h-9 rounded-lg ${i === 0 ? 'w-20 bg-blue-200' : 'w-24 bg-gray-200'}`} />
          ))}
        </div>
        {/* Row 1: 5 micro-card skeletons */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gray-100" />
                <div className="h-3 bg-gray-200 rounded w-24" />
              </div>
              <div className="h-7 bg-gray-200 rounded w-12 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-20" />
            </div>
          ))}
        </div>
        {/* Row 2: 3+2 grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div className="h-4 bg-gray-200 rounded w-36" />
              <div className="flex gap-1">
                {[...Array(3)].map((_, i) => <div key={i} className="h-7 bg-gray-100 rounded-md w-16" />)}
              </div>
            </div>
            <div className="h-44 bg-gray-100 rounded-xl mb-4" />
            <div className="flex gap-5 mb-6">
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-gray-200" /><div className="h-3 bg-gray-200 rounded w-28" /></div>
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-gray-200" /><div className="h-3 bg-gray-200 rounded w-20" /></div>
            </div>
            <div className="h-3 bg-gray-300 rounded w-32 mb-3" />
            <div className="space-y-2.5">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-violet-50/30 rounded-xl border border-violet-100/40">
                  <div className="w-8 h-8 rounded-lg bg-gray-200 flex-shrink-0" />
                  <div className="flex-1"><div className="h-3 bg-gray-200 rounded w-3/4 mb-1.5" /><div className="h-2.5 bg-gray-100 rounded w-1/3" /></div>
                  <div className="h-6 bg-gray-200 rounded-lg w-20" />
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
            <div className="h-4 bg-gray-200 rounded w-40 mb-6" />
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-gray-50/50 rounded-xl border border-gray-100">
                  <div className="w-10 h-10 rounded-xl bg-gray-200 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex justify-between mb-2">
                      <div className="h-3 bg-gray-200 rounded w-16" />
                      <div className="h-5 bg-gray-200 rounded-full w-14" />
                    </div>
                    <div className="h-6 bg-gray-100 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Row 3: 2-column */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div className="h-4 bg-gray-200 rounded w-36" />
                <div className="h-5 bg-gray-100 rounded-full w-20" />
              </div>
              <div className="space-y-4">
                {[...Array(4)].map((_, j) => (
                  <div key={j}>
                    <div className="flex justify-between mb-1.5"><div className="h-3 bg-gray-200 rounded w-24" /><div className="h-3 bg-gray-100 rounded w-28" /></div>
                    <div className="h-2.5 bg-gray-100 rounded-full w-full"><div className="h-2.5 bg-gray-200 rounded-full" style={{ width: `${25 + j * 18}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sleek, Non-Intrusive Channel Filter bar */}
      <div className="flex bg-white/80 backdrop-blur-sm p-1.5 rounded-xl border border-gray-150 shadow-sm max-w-max">
        {['Overall', 'LinkedIn', 'Instagram', 'YouTube', 'Facebook'].map((plat) => {
          const isActive = selectedPlatform === plat;
          const config = platformColors[plat];
          return (
            <button
              key={plat}
              onClick={() => setSelectedPlatform(plat)}
              className={`flex items-center gap-2.5 px-5 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${isActive
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                }`}
            >
              {plat === 'Overall' ? (
                <i className="ri-global-line text-sm" />
              ) : (
                <i className={`${config.icon.split(' ')[0]} text-sm ${isActive ? 'text-white' : ''}`} />
              )}
              {plat}
            </button>
          );
        })}
      </div>

      {govStatus === 'no-plan' && (
        <StatusBanner tone="info" title="No content plan for this month yet — create one in Content Planning Engine." />
      )}
      {isOtherMonth && (
        <StatusBanner tone="info" title={`Showing ${monthLabel(calendarMonth)} — no content plan for ${monthLabel(month)} yet.`} />
      )}
      {loadErrors.length > 0 && (
        <StatusBanner
          tone="error"
          title="Some of this dashboard couldn't load."
          message={loadErrors.join(' ')}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      )}

      {/* Row 1: Execution Overview — 5 micro-cards ("—" when governance data didn't load) */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Planned */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm transition-all hover:shadow-md">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
              <i className="ri-calendar-check-line text-[#5C4FCF] text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <span className="text-xs font-semibold text-gray-500">Total Planned Posts</span>
          </div>
          <p className="text-2.5xl font-black text-gray-900 leading-none">{govOk ? execStats.total_planned : '—'}</p>
          <p className="text-[10px] text-gray-400 font-medium mt-2">{calendarMonth}</p>
        </div>

        {/* Completed */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm transition-all hover:shadow-md">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
              <i className="ri-check-double-line text-emerald-600 text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <span className="text-xs font-semibold text-gray-500">Completed Posts</span>
          </div>
          <p className="text-2.5xl font-black text-emerald-600 leading-none">{govOk ? execStats.completed : '—'}</p>
          <p className="text-[10px] text-emerald-600/80 font-bold mt-2">{govOk ? `${completionRate}% rate` : '—'}</p>
        </div>

        {/* Pending */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm transition-all hover:shadow-md">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50/70 flex items-center justify-center">
              <i className="ri-time-line text-[#5C4FCF] text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <span className="text-xs font-semibold text-gray-500">Pending Posts</span>
          </div>
          <p className="text-2.5xl font-black text-[#5C4FCF] leading-none">{govOk ? execStats.pending : '—'}</p>
          <div className="mt-2.5">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-violet-50 text-[#5C4FCF] text-[10px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
              needs attention
            </span>
          </div>
        </div>

        {/* Overdue */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm transition-all hover:shadow-md">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
              <i className="ri-alert-line text-red-600 text-sm w-4 h-4 flex items-center justify-center" />
            </div>
            <span className="text-xs font-semibold text-gray-500">Overdue Posts</span>
          </div>
          <p className="text-2.5xl font-black text-red-600 leading-none">{govOk ? execStats.overdue : '—'}</p>
          <div className="mt-2.5">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-red-50 text-red-700 text-[10px] font-bold">
              critical attention
            </span>
          </div>
        </div>

        {/* Campaign Rate */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-center justify-between gap-4 transition-all hover:shadow-md">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                <i className="ri-funds-line text-[#5C4FCF] text-sm w-4 h-4 flex items-center justify-center" />
              </div>
              <span className="text-xs font-semibold text-gray-500">Campaign Rate</span>
            </div>
            <p className="text-[10px] text-gray-400 font-medium mt-2">
              {!govOk ? '—' : execStats.campaign ? `${execStats.campaign.total} campaign posts` : 'No campaigns this month'}
            </p>
          </div>
          <div className="flex flex-col items-center gap-1.5">
            <CircularProgress value={execStats.campaign?.campaign_rate ?? 0} />
            <span className="text-[10px] font-extrabold text-[#5C4FCF]">
              {!govOk ? '—' : execStats.campaign ? `${execStats.campaign.campaign_rate}%` : 'N/A'}
            </span>
        </div>
      </div>
    </div>

      {/* Row 2: Performance Overview & Platform Intelligence Matrix */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left 60% — Performance Overview */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-100 p-6 shadow-sm flex flex-col justify-between min-h-[480px]">
          <div>
            {/* Engagement only: reach history isn't stored, so there is no reach line or filter to show. */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-gray-900 text-sm">Performance Overview</h3>
            </div>

            {/* Recharts Area Chart — plots engagement_history from presence API */}
            <div className="h-44 w-full mb-4">
              {chartPointCount < 2 ? (
                <div className="h-full flex items-center justify-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                  <p className="text-xs text-gray-400">
                    {presenceError ? "Couldn't load engagement history." : 'Not enough history yet'}
                  </p>
                </div>
              ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorEngagement" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#5C4FCF" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#5C4FCF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 600 }}
                    tickFormatter={(val: string) => val ? val.split(' ')[0] : ''}
                  />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#94a3b8', fontWeight: 600 }} />
                  <RechartsTooltip contentStyle={{ borderRadius: '8px', border: '1px solid #f3f4f6', fontSize: '11px', fontWeight: 600 }} />
                  <Area type="monotone" dataKey="engagement" stroke="#5C4FCF" strokeWidth={2.5} fillOpacity={1} fill="url(#colorEngagement)" name="Engagement (%)" connectNulls={false} />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </div>

            <div className="flex items-center gap-5 mb-6">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#5C4FCF]" />
                <span className="text-[11px] font-bold text-gray-500">Engagement Trends</span>
              </div>
            </div>

            {/* Recently Completed — completed posts from governance API, newest first */}
            <div className="mb-6">
              <p className="text-[10px] font-extrabold text-gray-400 uppercase mb-3 tracking-wider">Recently Completed</p>
              <div className="space-y-2.5">
                {filteredPerformancePosts.map((post) => {
                  const conf = platformColors[post.platform];
                  return (
                    <div key={post.id} className="flex items-center gap-3 p-3 bg-violet-50/30 rounded-xl border border-violet-100/40">
                      <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0 border border-gray-100">
                        <i className={`${conf?.icon || 'ri-global-line'} text-sm`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-gray-900 truncate">{post.title}</p>
                        <p className="text-[10px] font-semibold text-gray-400">{post.platform}</p>
                      </div>
                      {post.scheduledDate && (
                        <span className="text-[10px] font-semibold text-gray-400 whitespace-nowrap">{post.scheduledDate}</span>
                      )}
                    </div>
                  );
                })}
                {filteredPerformancePosts.length === 0 && (
                  <p className="text-xs text-gray-400 italic py-2">
                    {govOk ? 'No completed posts recorded for this channel.' : govEmptyText}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Sentiment Donut — from presence API comment sentiment */}
          <div className="flex items-center gap-5 pt-4 border-t border-gray-100">
            <div
              className="w-16 h-16 rounded-full relative flex-shrink-0 shadow-sm flex items-center justify-center"
              style={{ background: conicGradientStr }}
            >
              <div className="absolute w-[72%] h-[72%] bg-white rounded-full flex items-center justify-center shadow-inner">
                <span className="text-xs font-extrabold text-gray-800">{sentimentPct('positive')}</span>
              </div>
            </div>
            <div className="flex-1 grid grid-cols-3 gap-2">
              <div className="p-2 bg-gray-50 rounded-lg border border-gray-105 flex flex-col justify-center">
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 mb-0.5">
                  <span className="w-2 h-2 rounded-full bg-[#5C4FCF]" />
                  <span>Positive</span>
                </div>
                <span className="text-sm font-black text-gray-900">{sentimentPct('positive')}</span>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg border border-gray-105 flex flex-col justify-center">
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 mb-0.5">
                  <span className="w-2 h-2 rounded-full bg-purple-300" />
                  <span>Neutral</span>
                </div>
                <span className="text-sm font-black text-gray-900">{sentimentPct('neutral')}</span>
              </div>
              <div className="p-2 bg-gray-50 rounded-lg border border-gray-105 flex flex-col justify-center">
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 mb-0.5">
                  <span className="w-2 h-2 rounded-full bg-red-300" />
                  <span>Negative</span>
                </div>
                <span className="text-sm font-black text-gray-900">{sentimentPct('negative')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right 40% — Platform Intelligence Matrix */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-6 shadow-sm flex flex-col justify-between min-h-[480px]">
          <div>
            <h3 className="font-bold text-gray-900 text-sm mb-6">Platform Intelligence Matrix</h3>
            {platformMatrices.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-4">
                {presenceError ? "Couldn't load platform data." : 'No connected platforms found.'}
              </p>
            ) : (
              <div className="space-y-4">
                {platformMatrices.map((pm: any) => {
                  const conf = platformColors[pm.name];
                  const isSelected = selectedPlatform === pm.name;
                  const spColor = pm.color || '#71717a';

                  return (
                    <div
                      key={pm.name}
                      onClick={() => setSelectedPlatform(pm.name)}
                      className={`flex items-center gap-4 p-4 rounded-xl border transition-all cursor-pointer ${isSelected
                          ? 'bg-blue-50/40 border-blue-200 shadow-sm'
                          : 'bg-gray-50/50 border-gray-100 hover:border-gray-200'
                        }`}
                    >
                      <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <i className={`${conf?.icon || 'ri-global-line'} text-lg`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-extrabold text-gray-900">{pm.name}</span>
                          <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                            {pm.score != null ? `${pm.score}/100` : '—'}
                          </span>
                        </div>
                        <div className="flex items-end justify-between gap-4">
                          <div>
                            <Sparkline data={pm.trend} color={spColor} />
                            <p className="text-[9px] font-bold text-gray-400 mt-1 uppercase tracking-wider">Engagement Trend</p>
                          </div>
                          <div className="text-right">
                            <span className={`text-[10px] font-black block ${pm.growth === '—' ? 'text-gray-400' : pm.growth.startsWith('-') ? 'text-rose-600' : 'text-emerald-600'}`}>{pm.growth}</span>
                            <span className="text-[8px] font-bold text-gray-400 uppercase block tracking-wider">Trend</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-6 p-4 bg-indigo-50/50 rounded-xl border border-indigo-100/60">
            <div className="flex items-center gap-2 mb-1.5">
              <i className="ri-bar-chart-grouped-line text-[#5C4FCF] text-sm" />
              <span className="text-xs font-bold text-indigo-900">Aggregate Health</span>
            </div>
            <p className="text-[11px] font-semibold text-indigo-950/80 leading-relaxed">
              {leadingPlatform
                ? `Platform ecosystem active. ${leadingPlatform} leads on health score.`
                : presenceError
                  ? "Couldn't load platform data."
                  : platformMatrices.length > 0
                    ? 'No health scores yet.'
                    : 'Connect platforms to see aggregate health.'}
            </p>
          </div>
        </div>
      </div>

      {/* Row 3: Theme Intelligence & Operational Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Theme Intelligence */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-gray-900 text-sm">Theme Intelligence</h3>
              <span className="text-[10px] font-extrabold text-[#5C4FCF] bg-indigo-50 border border-indigo-100 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                Consistency: {govOk ? `${completionRate}%` : '—'}
              </span>
            </div>
            {!govOk || themeAnalysis.insufficientData ? (
              <div className="flex items-center justify-center py-8 px-4 bg-gray-50 rounded-xl border border-gray-100 border-dashed">
                <p className="text-xs text-gray-400 text-center leading-relaxed">
                  {govOk ? 'Analysis can happen only after 5 or more posts have been made in the channel' : govEmptyText}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {themeAnalysis.themes.map((t: any) => (
                  <div key={t.name} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs font-semibold text-gray-700">
                      <span className="font-bold">{t.name}</span>
                      <span className="text-[10px] text-gray-400 font-bold">Done {t.best}% · Pending {t.low}%</span>
                    </div>
                    <ThemeBar best={t.best} low={t.low} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Operational Alerts Feed */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-gray-900 text-sm">Operational Alerts Feed</h3>
              <span className="text-[10px] font-extrabold text-gray-400 bg-gray-50 border border-gray-100 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                {govOk && !summaryError ? `${filteredAlerts.length} active` : '—'}
              </span>
            </div>
            <div className="space-y-3">
              {filteredAlerts.map((alert: any) => {
                const meta = alertMeta[alert.severity] || alertMeta.info;
                return (
                  <div key={alert.id} className={`p-4 rounded-xl border transition-all ${meta.border} ${meta.bg}`}>
                    <div className="flex items-start gap-3">
                      <div className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${meta.badgeBg} ${meta.badgeText}`}>
                        {alert.badge}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-gray-900 mb-0.5">{alert.title}</p>
                        <p className="text-[11px] font-medium text-gray-500 leading-normal">{alert.message}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredAlerts.length === 0 && (
                <div className="p-8 text-center text-xs text-gray-400 italic bg-gray-50 rounded-xl border border-gray-100 border-dashed">
                  {!govOk ? govEmptyText : summaryError ? "Couldn't load this month's alerts." : 'No active operational alerts for this channel.'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
